/**
 * Installations page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listInstallations, createInstallation, deleteInstallation } from '../services/installations';
import type { Installation } from '../../../shared/src/types';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Installations.css';

export default function Installations() {
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreate = user?.role === 'Admin' || user?.role === 'Supervisor' || user?.role === 'Técnico';
  const canDelete = user?.role === 'Admin';

  useEffect(() => {
    loadInstallations();
  }, []);

  const loadInstallations = async () => {
    try {
      setLoading(true);
      setError('');
      // List only active installations (exclude archived)
      const data = await listInstallations({ status: 'active' });
      setInstallations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading installations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    try {
      setCreating(true);
      setError('');
      
      const data = {
        name: formData.get('name') as string,
        type: formData.get('type') as 'residential' | 'industrial' | 'commercial',
        address: formData.get('address') as string || undefined,
      };

      await createInstallation(data);
      setShowCreateForm(false);
      await loadInstallations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating installation');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta instalación?')) {
      return;
    }

    try {
      setError('');
      await deleteInstallation(id);
      await loadInstallations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting installation');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'completed':
        return 'status-completed';
      case 'archived':
        return 'status-archived';
      default:
        return '';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'residential':
        return 'Residencial';
      case 'industrial':
        return 'Industrial';
      case 'commercial':
        return 'Comercial';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="installations">
        <div className="page-header">
          <h1>Instalaciones</h1>
        </div>
        <div className="loading">Cargando instalaciones...</div>
      </div>
    );
  }

  return (
    <div className="installations">
      <div className="page-header">
        <h1>Instalaciones</h1>
        {canCreate && (
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancelar' : 'Nueva instalación'}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && canCreate && (
        <div className="create-form-card">
          <h2>Crear nueva instalación</h2>
          <form onSubmit={handleCreate}>
            <div className="form-field">
              <label htmlFor="name">Nombre *</label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="Ej: Instalación Eléctrica Principal"
              />
            </div>

            <div className="form-field">
              <label htmlFor="type">Tipo *</label>
              <select id="type" name="type" required>
                <option value="">Seleccionar tipo</option>
                <option value="residential">Residencial</option>
                <option value="industrial">Industrial</option>
                <option value="commercial">Comercial</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="address">Dirección</label>
              <input
                id="address"
                name="address"
                type="text"
                placeholder="Dirección completa"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={creating}>
                {creating ? 'Creando...' : 'Crear instalación'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {installations.length === 0 ? (
        <div className="empty-state">
          <p>No hay instalaciones registradas</p>
          {canCreate && (
            <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
              Crear instalación
            </button>
          )}
        </div>
      ) : (
        <div className="installations-list">
          {installations.map((installation) => (
            <div key={installation.id} className="installation-card">
              <div className="card-header">
                <h3>{installation.name}</h3>
                <span className={`status-badge ${getStatusColor(installation.status)}`}>
                  {installation.status === 'active' ? 'Activa' : 
                   installation.status === 'completed' ? 'Completada' : 'Archivada'}
                </span>
              </div>
              <div className="card-body">
                <div className="card-info">
                  <span className="info-label">Tipo:</span>
                  <span className="info-value">{getTypeLabel(installation.type)}</span>
                </div>
                {installation.address && (
                  <div className="card-info">
                    <span className="info-label">Dirección:</span>
                    <span className="info-value">{installation.address}</span>
                  </div>
                )}
                <div className="card-info">
                  <span className="info-label">Creada:</span>
                  <span className="info-value">
                    {new Date(installation.created_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>
              <div className="card-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/installations/${installation.id}`)}
                >
                  Ver detalle
                </button>
                {canDelete && (
                  <button
                    className="btn-destructive"
                    onClick={() => handleDelete(installation.id)}
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
